import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar2 from '../component/navbar2';
import Footer from '../components/BrandFooter';
import Dh from '../components/Dh';

// ── palette ───────────────────────────────────────────────────────────────────
const BRAND      = '#1976d2';
const BRAND_DEEP = '#3171af';
const BRAND_BRT  = '#5B9BD5';
const ICE        = '#E8F2FD';
const ICE2       = '#e1f5fe';
const CREAM      = '#FCFDFF';
const PAPER      = '#F1F0E4';
const INK        = '#17263A';
const INK2       = '#42505F';
const MUTED      = '#6E7888';
const LINE       = '#E5E8EA';
const WHITE      = '#ffffff';

// score-band colours
const CRIT  = '#C0392B';
const VHIGH = '#E0592A';
const HIGH  = '#E08A1E';
const MED   = '#E6B400';
const SEMIC = '#1976d2';
const READY = '#0E8F6E';
const EXC   = '#15824E';

// ── static data ───────────────────────────────────────────────────────────────
const MULT = 1.515;

type Band = [string, number, string, string];
const BANDS: Band[] = [
  ['Critical Risk',   22,       CRIT,  'Major foundational gaps; not yet fundable.'],
  ['Very High Risk',  27,       VHIGH, 'Severe issues; extensive work needed.'],
  ['High Risk',       32,       HIGH,  'Key gaps exist; limited investor appeal.'],
  ['Medium Risk',     37,       MED,   'Some readiness; improvements required.'],
  ['Semi-Ready',      43,       SEMIC, 'Good preparation; needs polishing.'],
  ['Investor-Ready',  49,       READY, 'Fully prepared; strong funding potential.'],
  ['Exceptional',     Infinity, EXC,   'Exemplary readiness; a standout investment.'],
];

// precompute band rows (lo accumulates across bands)
let _lo = 0;
const BAND_ROWS = BANDS.map(([name, max, color, desc]) => {
  const hi  = max === Infinity ? '+' : Math.round(max * MULT);
  const rng = `${Math.round(_lo * MULT)}–${hi}`;
  if (max !== Infinity) _lo = max;
  return { name, color, desc, rng };
});

const TQ_OPTS: Record<string, [string, number][]> = {
  runway:  [['24 months+', 5], ['12–24 months', 4], ['6–12 months', 2], ['Under 6 months', 1]],
  revenue: [['Recurring', 5], ['Mostly stable', 4], ['Irregular', 2], ['None yet', 1]],
  risk:    [['Moderate, calibrated', 5], ['Comfortable with big risk', 3], ['Prefer minimal risk', 1]],
};
const TQ_LABELS: Record<string, string> = {
  runway:  'Your current runway?',
  revenue: 'Your revenue today?',
  risk:    'Your appetite for risk?',
};
const TEASER_KEYS = ['runway', 'revenue', 'risk'];

const PRICES = {
  AED: { p1: '199', p2: '365' },
  USD: { p1: '54',  p2: '99'  },
};

function detectCurrency(): 'AED' | 'USD' {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (/Asia\/(Dubai|Muscat|Qatar|Riyadh|Bahrain|Kuwait)/i.test(tz)) return 'AED';
  } catch { /* */ }
  return 'USD';
}

// ── helpers ───────────────────────────────────────────────────────────────────
function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref  = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  const t    = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e?.isIntersecting) { t.current = setTimeout(() => setVis(true), delay); obs.disconnect(); }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => { obs.disconnect(); if (t.current) clearTimeout(t.current); };
  }, [delay]);
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}>
      {children}
    </div>
  );
}

function Check() {
  return (
    <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke={EXC} strokeWidth="2.6" style={{ flexShrink: 0, marginTop: 3 }}>
      <path d="M5 13l4 4L19 7"/>
    </svg>
  );
}


function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: BRAND, background: ICE, display: 'inline-flex', alignItems: 'center', padding: '6px 14px', borderRadius: 999 }}>
      {children}
    </span>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────
export default function CapitalReadiness() {
  const [tstate,   setTstate]   = useState<Record<string, number>>({});
  const [currency, setCurrency] = useState<'AED' | 'USD'>(() => detectCurrency());
  const [openFaq,  setOpenFaq]  = useState<number | null>(null);

  // teaser computation
  const vals      = TEASER_KEYS.map(k => tstate[k]).filter((v): v is number => v != null);
  const pct       = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / (TEASER_KEYS.length * 5) * 100) : 0;
  const answered  = vals.length === TEASER_KEYS.length;
  const arcOffset = vals.length ? 264 - 264 * pct / 100 : 264;

  let meterColor = BRAND_BRT;
  let tMsg       = 'Answer 3 quick questions';
  if (vals.length && !answered) tMsg = 'Keep going…';
  if (answered) {
    if (pct >= 80) { meterColor = EXC;   tMsg = 'Looking strong — see your full score'; }
    else if (pct >= 60) { meterColor = READY; tMsg = 'On track — a few gaps to close'; }
    else if (pct >= 40) { meterColor = MED;   tMsg = 'Some real gaps to address'; }
    else                { meterColor = CRIT;  tMsg = 'Needs work before you raise'; }
  }

  const price = (k: 'p1' | 'p2') => PRICES[currency][k];

  const FAQS = [
    { q: 'Is the quick check the same as my full score?',
      a: 'No — the quick check is an indicative read from 3 factors. Your full Investor Readiness Score uses all 14 and is free once you create an account.' },
    { q: 'What do I pay for?',
      a: 'The score is free. You pay a one-time fee to unlock the full report — the detailed breakdown, peer benchmark and action plan. The higher tier adds a 1-hour advisory session.' },
    { q: 'Is this a subscription?',
      a: 'No. Both options are one-time payments — no recurring charges.' },
    { q: 'Is this financial advice?',
      a: "No. It's a readiness indicator to help you prepare; it doesn't guarantee funding and isn't investment advice." },
  ];

  return (
    <>
      {/* ── sticky nav ── */}
      <div className="sticky top-0 z-50 w-full">
        <Navbar2 bgColor="[#E8F2FD]" logoTo="/" signupTo="/consulting/signup" />
      </div>

      {/* ── HERO ── */}
      <section style={{ position: 'relative', background: `radial-gradient(120% 90% at 82% -10%, ${ICE} 0%, ${CREAM} 55%)`, overflow: 'hidden' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '72px 24px 0' }}>
          <div className="grid grid-cols-1 lg:[grid-template-columns:1.05fr_0.95fr] gap-8 items-center pb-8">

            {/* left */}
            <FadeIn delay={50}>
              <Eyebrow>Capital Readiness</Eyebrow>
              <h1 style={{ marginTop: 14, fontSize: 'clamp(38px,5vw,60px)', fontWeight: 900, color: INK, lineHeight: 1.04, letterSpacing: '-0.03em' }}>
                How investor-ready are you,{' '}
                <span style={{ position: 'relative', display: 'inline-block' }}>
                  really?
                  <span style={{ position: 'absolute', left: -2, right: -2, bottom: 6, height: '0.3em', background: ICE2, zIndex: -1, transform: 'skewX(-4deg)', display: 'block' }} />
                </span>
              </h1>
              <p style={{ marginTop: 22, marginBottom: 28, fontSize: 19, color: INK2, maxWidth: 560, lineHeight: 1.6 }}>
                Try the 3-question quick check for an instant read — then create your account to run the full 14-factor Investor Readiness Score and unlock your report.
              </p>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <a href="#teaser" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, background: BRAND, color: WHITE, border: `2px solid ${BRAND_DEEP}`, textDecoration: 'none' }}>
                  Try the quick check →
                </a>
                <a href="#how" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, background: 'transparent', color: INK, border: `2px solid ${INK}`, textDecoration: 'none' }}>
                  How it works
                </a>
              </div>
              <div style={{ marginTop: 22, fontSize: 13.5, color: MUTED }}>
                3-question taster · full score in-platform · MENA · UK · USA
              </div>
            </FadeIn>

            {/* teaser card */}
            <div id="teaser">
              <FadeIn delay={200}>
                <div style={{ background: WHITE, border: `1px solid ${LINE}`, borderRadius: 24, boxShadow: '0 26px 60px -24px rgba(23,38,58,.30)', overflow: 'hidden' }}>
                  {/* dark top */}
                  <div style={{ background: 'linear-gradient(120deg,#10243b,#1b3550)', padding: '20px 24px', textAlign: 'center' }}>
                    <div style={{ fontWeight: 800, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#9fb0c9' }}>
                      Quick indicative read
                    </div>
                    {/* arc meter */}
                    <div style={{ position: 'relative', width: 170, height: 102, margin: '8px auto 0' }}>
                      <svg viewBox="0 0 200 120" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                        <path d="M16 110 A 84 84 0 0 1 184 110" fill="none" stroke="#22324c" strokeWidth="15" strokeLinecap="round"/>
                        <path d="M16 110 A 84 84 0 0 1 184 110" fill="none" stroke={meterColor}
                          strokeWidth="15" strokeLinecap="round"
                          strokeDasharray="264" strokeDashoffset={arcOffset}
                          style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.3s ease' }}
                        />
                      </svg>
                      <div style={{ position: 'absolute', left: 0, right: 0, top: 44, textAlign: 'center' }}>
                        <b style={{ fontSize: 36, fontWeight: 900, display: 'block', lineHeight: 1, color: WHITE }}>
                          {vals.length ? pct : '—'}
                        </b>
                        <span style={{ fontSize: 10.5, color: '#9fb0c9', fontWeight: 700 }}>indicative</span>
                      </div>
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 14, marginTop: 6, color: '#FFE38A', minHeight: 20 }}>
                      {tMsg}
                    </div>
                  </div>

                  {/* question body */}
                  <div style={{ padding: '18px 22px 22px' }}>
                    {TEASER_KEYS.map(key => (
                      <div key={key} style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 7, color: INK }}>{TQ_LABELS[key]}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {TQ_OPTS[key].map(([label, val]) => {
                            const sel = tstate[key] === val;
                            return (
                              <button key={label} onClick={() => setTstate(s => ({ ...s, [key]: val }))}
                                style={{ border: `1.5px solid ${sel ? BRAND : LINE}`, borderRadius: 9, padding: '7px 11px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', color: sel ? INK : INK2, background: sel ? ICE : WHITE, transition: 'border-color .15s, background .15s', fontFamily: 'inherit' }}>
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}

                    <Link to="/consulting/signup?service=capital"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, background: BRAND, color: WHITE, border: `2px solid ${BRAND_DEEP}`, textDecoration: 'none', marginTop: 6 }}>
                      Get my full score →
                    </Link>
                    <div style={{ fontSize: 11, color: MUTED, textAlign: 'center', marginTop: 9, fontWeight: 600 }}>
                      This is an indicative read from 3 of 14 factors. Your full score is free — the report is a one-time unlock.
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>

        {/* chain divider */}
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how" style={{ padding: '60px 0 84px' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 24px' }}>
          <FadeIn className="text-center max-w-[760px] mx-auto">
            <Eyebrow>How it works</Eyebrow>
            <h2 style={{ fontSize: 'clamp(30px,4.2vw,46px)', marginTop: 14, marginBottom: 12, fontWeight: 900, color: INK }}>
              A real readiness read in 3 steps
            </h2>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[18px] mt-[38px]">
            {[
              { n: 1, title: 'Try the quick check', desc: 'Three questions, an instant indicative read — no signup needed to start.' },
              { n: 2, title: 'Get your full score', desc: 'Create your account and answer all 14 factors investors weigh. Your score is free.' },
              { n: 3, title: 'Unlock your report', desc: 'A detailed breakdown, peer benchmark and action plan — with an optional advisory session.' },
            ].map((s, i) => (
              <FadeIn key={s.n} delay={i * 120}>
                <div style={{ background: WHITE, border: `1px solid ${LINE}`, borderRadius: 18, padding: 24, height: '100%' }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: ICE, color: BRAND, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{s.n}</div>
                  <h3 style={{ fontSize: 17, margin: '13px 0 6px', fontWeight: 800, color: INK }}>{s.title}</h3>
                  <p style={{ fontSize: 14, color: INK2 }}>{s.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" style={{ background: PAPER, padding: '68px 0 84px' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 24px' }}>
          <FadeIn className="text-center max-w-[760px] mx-auto">
            <Eyebrow>Pricing</Eyebrow>
            <h2 style={{ fontSize: 'clamp(30px,4.2vw,46px)', marginTop: 14, marginBottom: 12, fontWeight: 900, color: INK }}>
              Your score is free. Unlock the report.
            </h2>
            <p style={{ fontSize: 19, color: INK2 }}>One-time, no subscription. Choose your currency.</p>
          </FadeIn>

          {/* currency toggle */}
          <div style={{ textAlign: 'center', marginTop: 18 }}>
            <span style={{ display: 'inline-flex', border: `1.5px solid ${LINE}`, borderRadius: 10, overflow: 'hidden', background: WHITE }}>
              <button onClick={() => setCurrency('AED')}
                style={{ border: 'none', background: currency === 'AED' ? INK : WHITE, color: currency === 'AED' ? WHITE : MUTED, fontFamily: 'inherit', fontWeight: 800, fontSize: 13, padding: '9px 20px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Dh />AED
              </button>
              <button onClick={() => setCurrency('USD')}
                style={{ border: 'none', background: currency === 'USD' ? INK : WHITE, color: currency === 'USD' ? WHITE : MUTED, fontFamily: 'inherit', fontWeight: 800, fontSize: 13, padding: '9px 20px', cursor: 'pointer' }}>
                USD
              </button>
            </span>
          </div>

          {/* tiers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-[760px] mx-auto mt-[34px]">
            {/* report */}
            <FadeIn delay={0}>
              <div style={{ background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 20, padding: '28px 26px', display: 'flex', flexDirection: 'column', height: '100%' }}>
                <h3 style={{ fontSize: 20, fontWeight: 800, color: INK }}>Report</h3>
                <p style={{ fontSize: 13, color: MUTED, minHeight: 38, marginTop: 4 }}>The full readiness report to act on before you raise.</p>
                <div style={{ marginTop: 10 }}>
                  <b style={{ fontSize: 38, fontWeight: 900, letterSpacing: '-0.03em', color: INK }}>
                    {currency === 'AED' ? <><Dh />{price('p1')}</> : `$${price('p1')}`}
                  </b>
                </div>
                <div style={{ fontSize: 11.5, color: MUTED, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>one-time</div>
                <ul style={{ listStyle: 'none', margin: '18px 0 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {['Full 14-factor breakdown', 'Peer benchmark by stage', 'Prioritised action plan (PDF)'].map(f => (
                    <li key={f} style={{ display: 'flex', gap: 10, fontSize: 14, color: INK2, alignItems: 'flex-start' }}><Check />{f}</li>
                  ))}
                </ul>
                <Link to={`/consulting/signup?service=capital&plan=report&cur=${currency}`}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, background: INK, color: CREAM, border: `2px solid ${INK}`, textDecoration: 'none', marginTop: 'auto' }}>
                  Unlock report
                </Link>
              </div>
            </FadeIn>

            {/* report + advisory */}
            <FadeIn delay={120}>
              <div style={{ position: 'relative', background: WHITE, border: `1.5px solid ${BRAND}`, borderRadius: 20, padding: '28px 26px', display: 'flex', flexDirection: 'column', boxShadow: '0 26px 60px -24px rgba(23,38,58,.30)', height: '100%' }}>
                <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: BRAND, color: WHITE, fontSize: 12, fontWeight: 800, padding: '4px 14px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                  Best value
                </div>
                <h3 style={{ fontSize: 20, fontWeight: 800, color: INK }}>Report + advisory</h3>
                <p style={{ fontSize: 13, color: MUTED, minHeight: 38, marginTop: 4 }}>The report plus an hour with our advisory team to plan your raise.</p>
                <div style={{ marginTop: 10 }}>
                  <b style={{ fontSize: 38, fontWeight: 900, letterSpacing: '-0.03em', color: INK }}>
                    {currency === 'AED' ? <><Dh />{price('p2')}</> : `$${price('p2')}`}
                  </b>
                </div>
                <div style={{ fontSize: 11.5, color: MUTED, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>one-time</div>
                <ul style={{ listStyle: 'none', margin: '18px 0 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <li style={{ display: 'flex', gap: 10, fontSize: 14, color: INK2, alignItems: 'flex-start' }}><Check />Everything in Report</li>
                  <li style={{ display: 'flex', gap: 10, fontSize: 14, color: INK2, alignItems: 'flex-start' }}><Check /><b>1-hour advisory session</b></li>
                  <li style={{ display: 'flex', gap: 10, fontSize: 14, color: INK2, alignItems: 'flex-start' }}><Check />Tailored raise game-plan</li>
                </ul>
                <Link to={`/consulting/signup?service=capital&plan=report-session&cur=${currency}`}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, background: BRAND, color: WHITE, border: `2px solid ${BRAND_DEEP}`, textDecoration: 'none', marginTop: 'auto' }}>
                  Unlock + book session
                </Link>
              </div>
            </FadeIn>
          </div>

          <p style={{ textAlign: 'center', marginTop: 18, color: MUTED, fontSize: 13 }}>
            {currency === 'AED'
              ? 'Shown in dirhams for UAE clients — switch to USD anytime.'
              : 'Shown in USD for international clients — switch to dirhams anytime.'}
          </p>
        </div>
      </section>

      {/* ── SCORE BANDS ── */}
      <section id="bands" style={{ padding: '84px 0' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 24px' }}>
          <FadeIn className="text-center max-w-[760px] mx-auto">
            <Eyebrow>Score bands</Eyebrow>
            <h2 style={{ fontSize: 'clamp(30px,4.2vw,46px)', marginTop: 14, marginBottom: 12, fontWeight: 900, color: INK }}>
              What your score means
            </h2>
          </FadeIn>
          <FadeIn className="mt-[30px] flex flex-col gap-2 max-w-[900px] mx-auto">
            {BAND_ROWS.map(({ name, color, desc, rng }) => (
              <div key={name} className="grid grid-cols-1 md:[grid-template-columns:14px_160px_1fr]"
                style={{ gap: 14, alignItems: 'center', background: WHITE, border: `1px solid ${LINE}`, borderRadius: 12, padding: '12px 16px' }}>
                <span style={{ width: 14, height: 14, borderRadius: '50%', background: color, display: 'block', flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14.5, color: INK }}>{name}</div>
                  <div style={{ fontSize: 11.5, color: MUTED, fontWeight: 700 }}>score {rng}</div>
                </div>
                <div style={{ fontSize: 13, color: INK2 }}>{desc}</div>
              </div>
            ))}
          </FadeIn>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" style={{ background: PAPER, padding: '84px 0' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 24px' }}>
          <FadeIn className="text-center">
            <Eyebrow>FAQ</Eyebrow>
            <h2 style={{ fontSize: 'clamp(28px,4vw,42px)', marginTop: 14, fontWeight: 900, color: INK }}>
              About the assessment
            </h2>
          </FadeIn>
          <div style={{ maxWidth: 800, margin: '26px auto 0' }}>
            {FAQS.map((faq, i) => (
              <FadeIn key={i} delay={i * 60}>
                <div style={{ borderBottom: `1px solid ${LINE}` }}>
                  <button onClick={() => setOpenFaq(p => p === i ? null : i)}
                    style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left', padding: '20px 4px', fontSize: 17, fontFamily: 'inherit', fontWeight: 700, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 16, color: INK }}>
                    <span>{faq.q}</span>
                    <span style={{ fontSize: 24, color: BRAND, transition: 'transform 0.3s', transform: openFaq === i ? 'rotate(45deg)' : 'none', flexShrink: 0 }}>+</span>
                  </button>
                  <div style={{ overflow: 'hidden', maxHeight: openFaq === i ? 260 : 0, transition: 'max-height 0.32s ease', color: INK2, fontSize: 15 }}>
                    <p style={{ padding: '0 4px 20px' }}>{faq.a}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={{ padding: '84px 0' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '0 24px' }}>
          <FadeIn>
            <div style={{ background: INK, borderRadius: 30, padding: '62px 40px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(${BRAND_BRT} 0.9px, transparent 0.9px)`, backgroundSize: '24px 24px', opacity: 0.12 }} />
              <h2 style={{ fontSize: 'clamp(30px,4.4vw,48px)', position: 'relative', color: WHITE, fontWeight: 900 }}>
                See where you really stand.
              </h2>
              <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.9)', maxWidth: 560, margin: '14px auto 26px', position: 'relative' }}>
                Create your account, get your full Investor Readiness Score for free, and unlock the report when you're ready.
              </p>
              <Link to="/consulting/signup?service=capital"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '14px 26px', borderRadius: 999, fontWeight: 700, fontSize: 15, background: BRAND, color: WHITE, border: `2px solid ${BRAND_DEEP}`, textDecoration: 'none', position: 'relative' }}>
                Get my full score →
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </>
  );
}
